import { BaseUtils } from "tnbase";
import { Storey } from "@/declare/interface/project/storey";
import { useCallback, useState } from "react";

export interface PositionStatus {
    axisNetUuids: string[] // 当前需要在空间定位显示的轴网uuid列表
    updateAxisNetUuids: (value: string[]) => void // 更新显示轴网
    activeStorey: Storey // 当前正在编辑的楼层
    editingStorey: (value: Storey) => void // 当前正在编辑的楼层
    pushAxisNetToArray: (value: string[]) => void // 追加轴网图元
    spliceAxisNetToArray: (value: string[]) => void // 去除轴网图元
    toggleAxisNetToArray: (value: string[]) => void // 切换轴网图元
    clear: () => void // 清空上下文
    renderFlag: string // 触发重新渲染的标志
    render: () => void // 触发重新渲染
    fastGenVisible: boolean // 快速生成面板是否可见
    setFastGenVisible: (value: boolean) => void // 更新快速生成面板是否可见
}

export function usePositionService() {
    // 当前需要在空间定位显示的轴网
    const [axisNetUuids, setAxisNetUuids] = useState<string[]>([]);
    // 当前可以编辑的轴网图元
    const [activeStorey, setActiveStorey] = useState<Storey>(null);
    // 触发重新渲染的标志
    const [renderFlag, setRenderFlag] = useState<string>('');
    // 快速生成面板是否可见
    const [fastGenVisible, setFastGenVisible] = useState<boolean>(false);

    // 更新显示轴网
    const updateAxisNetUuids = useCallback((value: string[]) => {
        setAxisNetUuids([...value || []]);
    }, [axisNetUuids]);

    // 设置正在编辑的楼层
    const editingStorey = useCallback((value: Storey) => {
        setActiveStorey(value);
    }, [activeStorey]);

    // 追加轴网图元
    const pushAxisNetToArray = useCallback((value: string[]) => {
        setAxisNetUuids(prevUuids => {
            // 检查是否有新元素需要添加
            const newElements = value.filter(item => !prevUuids.includes(item));
            if (newElements.length === 0) {
                // 如果没有新元素需要添加，则不触发更新
                return prevUuids;
            }
            // 创建一个新的 Set，合并原数组和新数组，然后展开为数组
            const uniqueUuids = Array.from(new Set([...prevUuids, ...value]));
            return uniqueUuids;
        });

    }, [axisNetUuids]);

    // 去除轴网图元
    const spliceAxisNetToArray = useCallback((value: string[]) => {
        if (!value || value?.length === 0) return;
        const newArr = axisNetUuids?.filter(item => !value?.some(v => v === item)) || [];
        setAxisNetUuids(newArr);
    }, [axisNetUuids]);

    // 切换显示
    const toggleAxisNetToArray = useCallback((value: string[]) => {
        setAxisNetUuids(prevUuids => {
            const newUuids = new Set(prevUuids);
            value.forEach(item => {
                if (newUuids.has(item)) {
                    newUuids.delete(item);
                } else {
                    newUuids.add(item);
                }
            });
            return Array.from(newUuids);
        });
    }, [axisNetUuids]);

    /**
     * 清空上下文
     */
    const clear = useCallback(() => {
        setAxisNetUuids([]);
        setActiveStorey(null);
        setFastGenVisible(false);
    }, [axisNetUuids, activeStorey])

    /**
     * 触发重新渲染
     */
    const render = () => {
        setRenderFlag(BaseUtils.genUUID());
    }

    return {
        axisNetUuids,
        updateAxisNetUuids,
        activeStorey,
        editingStorey,
        pushAxisNetToArray,
        spliceAxisNetToArray,
        toggleAxisNetToArray,
        clear,
        renderFlag,
        render,
        fastGenVisible,
        setFastGenVisible,
    } as PositionStatus;
}